/**
 * Background chat polling for the player PWA.
 *
 * Session 62: before this, `unreadChatCount` only reset to 0 in chat-view —
 * it was never incremented anywhere, so the badge on the "Чат" button
 * rendered but always showed 0. The chat-view WebSocket only mounts when
 * the player is already on /chat, so no incoming message could ever raise
 * the counter while the player was on a location screen.
 *
 * The fix is deliberately small: every POLL_INTERVAL_MS ask the server for
 * the current chat thread (HTTP, same endpoint the chat-view uses on open),
 * diff against the messages we've already acknowledged, and bump the
 * unread counter for any new row whose sender is ORGANIZER. The poll is
 * cheap (one SELECT filtered by gameId+teamName) and avoids adding a
 * second WebSocket to the mobile client.
 *
 * Start it once per join — typically at router bootstrap after the player
 * has a playerId/gameCode. Stop it on leaveGame/logout.
 */

import { getState, setState, type ChatMessage } from '../store';
import { getChatMessages } from '../api/game-api';

// 15s matches the cadence organizers usually send at without being so
// aggressive the server drops into poll-storm territory if many teams are
// live at once. Short enough that the badge doesn't look dead.
const POLL_INTERVAL_MS = 15_000;

let pollTimer: ReturnType<typeof setInterval> | null = null;
let lastSeenId: string | null = null;

async function pollOnce(): Promise<void> {
  const state = getState();
  if (!state.gameCode || !state.playerId) return;
  // Player is currently in chat-view → WS is already draining the stream.
  // Polling would race with the WS handler and double-count. Chat-view
  // zeros unreadChatCount on mount and again on back, so returning early
  // here keeps the invariant.
  if (window.location.hash.startsWith('#/chat')) return;

  let messages: ChatMessage[];
  try {
    messages = await getChatMessages(state.gameCode, state.playerId);
  } catch {
    return;
  }

  const after = state.gameCode ? getState() : null;
  // Navigation may have happened during the fetch. Re-check before writing.
  if (!after || after.gameCode !== state.gameCode) return;

  // First poll — take the latest id as the baseline without incrementing.
  // Otherwise a fresh device that joined mid-game would suddenly show
  // every historical organizer message as "unread".
  if (lastSeenId === null) {
    lastSeenId = messages.length > 0 ? messages[messages.length - 1].id : '';
    // Seed store so chat-view doesn't lose attribution on next open.
    setState({ chatMessages: messages });
    return;
  }

  // messages are timestamp-ascending; find where we left off.
  const cutoff = messages.findIndex((m) => m.id === lastSeenId);
  const freshSlice = cutoff === -1 ? messages : messages.slice(cutoff + 1);
  if (freshSlice.length === 0) return;

  const newOrgCount = freshSlice.filter((m) => m.senderType === 'ORGANIZER').length;
  lastSeenId = messages[messages.length - 1].id;
  setState({
    chatMessages: messages,
    unreadChatCount: after.unreadChatCount + newOrgCount,
  });
}

export function startChatPolling(): void {
  if (pollTimer) return;
  lastSeenId = null;
  // Immediate seed so the baseline is set quickly; subsequent polls detect deltas.
  pollOnce();
  pollTimer = setInterval(pollOnce, POLL_INTERVAL_MS);
}

export function stopChatPolling(): void {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  lastSeenId = null;
}

/**
 * chat-view calls this on mount so the next poll doesn't resurrect the
 * counter by treating messages the player just read as "new".
 */
export function acknowledgeChatThrough(messages: ChatMessage[]): void {
  if (messages.length > 0) {
    lastSeenId = messages[messages.length - 1].id;
  }
}
